import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  DialogTitle,
  DialogActions,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import { Add, Close, Edit, Visibility } from "@mui/icons-material";
import axios from "axios";
import { useToast } from "../../components/Toast";
import { useNavigate, useParams } from "react-router-dom";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import { Loading } from "../../components/Loading";
import { uploadFile } from "../../utils/UploadFile";
import { LocalUser } from "../../utils/UploadFile";
import { fetchDocuments } from "../../utils/FetchDocuments";
import { DocumentUploadSection } from "../../components/DocumentUploadSection";
import NavigationHeader from "../../components/NavigationHeader";
import { formatDate } from "../../utils/utils";

interface Document {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  s3Key: string;
  s3Url: string;
  documentContext: string;
  documentType: string;
  documentTypeId: string;
  companyId: string;
  uploadedBy: string;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  presignedUrl: string;
  file?: File;
}

interface UploadedFile {
  id: string;
  fileName: string;
  presignedUrl: string;
  file: File;
}

const VehicleDocument = () => {
  const { vehicleId } = useParams();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { user, loading } = useFirebaseAuth();
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : "";
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documentsData, setDocumentsData] = useState<Document[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [documentForm, setDocumentForm] = useState({
    documentType: "",
    description: "",
    id: "",
  });

  useEffect(() => {
    if (user && vehicleId && !loading) {
      fetchBaicDocuments();
    }
  }, [user, vehicleId, loading]);

  const fetchBaicDocuments = async () => {
    try {
      const documents = await fetchDocuments({
        documentContext: "vehicle",
        documentTypeId: vehicleId as string,
      });
      setDocumentsData(documents || []);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching documents:", error);
      showToast("Failed to fetch documents", "error");
      setIsLoading(false);
    }
  };

  const handlePreview = (url: string) => {
    window.open(url, "_blank");
  };

  const handleDocumentSubmit = async () => {
    // Creating a new document
    const isNew = !documentForm.id;

    if (isNew) {
      if (!selectedFiles) {
        showToast("Please select a file", "error");
        return;
      }
    } else {
      // Updating an existing document
      const isUploadingNewFile = !!selectedFiles;
      const hasExistingFile = uploadedFiles.length > 0;

      if (!hasExistingFile && !isUploadingNewFile) {
        showToast("Please upload a file", "error");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const response = await uploadFile({
        ...(documentForm.id && { isUpdate: true, documentId: documentForm.id }),
        file: selectedFiles || uploadedFiles[0].file,
        documentType: documentForm.documentType,
        documentContext: "vehicle",
        description: documentForm.description,
        documentTypeId: vehicleId as string,
        companyId,
      });
      if (response) {
        showToast("Document uploaded successfully", "success");
        setIsDialogOpen(false);
        setSelectedFiles(null);
        setUploadedFiles([]);
        setDocumentForm({ documentType: "", description: "", id: "" });
        fetchBaicDocuments();
      } else {
        showToast("Failed to upload document", "error");
      }
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditDocument = (document: Document) => {
    setIsDialogOpen(true);
    setSelectedFiles(null);
    setDocumentForm({
      documentType: document.documentType,
      description: document.description,
      id: document.id,
    });
    if (document.presignedUrl) {
      setUploadedFiles([
        {
          id: document.id,
          fileName: document.fileName,
          presignedUrl: document.presignedUrl,
          file: document.file || new File([], document.fileName),
        },
      ]);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedFiles(null);
    setUploadedFiles([]);
    setDocumentForm({ documentType: "", description: "", id: "" });
  };

  if (loading || isLoading) {
    return <Loading message={`Loading documents...`} fullScreen={true} />;
  }

  if (isSubmitting) {
    return <Loading message={`Uploading document...`} fullScreen={true} />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Vehicles", path: "/vehicles" },
          { label: "Vehicle Details", path: `/vehicles/${vehicleId}` },
          { label: "Vehicle Documents" },
        ]}
      />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Vehicle Documents</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          sx={{ px: 1, py: 0.5 }}
          onClick={() => setIsDialogOpen(true)}
        >
          Add New Document
        </Button>
      </Box>
      <Paper sx={{ width: "100%", mb: 4 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Document Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Uploaded At</TableCell>
                <TableCell>File Name</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documentsData?.map((document) => (
                <TableRow key={document.id}>
                  <TableCell>{document.documentType}</TableCell>
                  <TableCell>{document.description || "-"}</TableCell>
                  <TableCell>{formatDate(document.createdAt)}</TableCell>
                  <TableCell>{document.fileName}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconButton
                        onClick={() => handlePreview(document.presignedUrl)}
                        size="small"
                        sx={{ color: "primary.main" }}
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        onClick={() => handleEditDocument(document)}
                        size="small"
                        sx={{ color: "primary.main" }}
                      >
                        <Edit />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {(!documentsData || !documentsData?.length) && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No documents found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Document Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            {selectedFiles ? "Edit Document" : "Add New Document"}
            <IconButton onClick={handleCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                type="text"
                label="Document Type"
                value={documentForm.documentType}
                onChange={(e) =>
                  setDocumentForm((prev) => ({
                    ...prev,
                    documentType: e.target.value,
                  }))
                }
                placeholder="RC, Fitness, etc."
                fullWidth
                required
              ></TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={documentForm.description}
                onChange={(e) =>
                  setDocumentForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <DocumentUploadSection
                title="Upload Document"
                selectedFiles={selectedFiles ? [selectedFiles] : []}
                uploadedFiles={uploadedFiles}
                onFileSelect={(files: File[]) => {
                  setSelectedFiles(files[0] || null);
                }}
                onFileRemove={() => setSelectedFiles(null)}
                onPreview={handlePreview}
                isLoading={isUploading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDocumentSubmit}
            disabled={
              isSubmitting ||
              (!selectedFiles && !uploadedFiles) ||
              !documentForm.documentType
            }
          >
            {isSubmitting ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VehicleDocument;
